/**
 * The exit-criteria checklist for a startup's current stage (PRD §5.3).
 *
 * "The app shows them as a checklist and warns (does not block) on advance."
 * Each row is a `Gate`, which carries the met/not-met state in words as well as
 * in the disc — PRD §9.7 does not permit status by colour alone, and a list of
 * green and grey ticks is exactly that.
 *
 * The note beneath is the half people miss: seeing unmet criteria, a reader
 * reasonably assumes the stage is locked. It is not, and saying so here is
 * cheaper than letting them discover it by not trying.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 6.5, 6.7, 6.5.9, PRD §5.3
 */
import { useTranslation } from 'react-i18next';

import { Gate } from '../../../components/ui/Gate';
import { Note } from '../../../components/ui/Note';

export interface ExitCriteriaChecklistProps {
  items: readonly { key: string; label: string; isMet: boolean }[];
}

export function ExitCriteriaChecklist({
  items,
}: ExitCriteriaChecklistProps): React.JSX.Element | null {
  const { t } = useTranslation();

  /* A terminal stage has no exit criteria; an empty heading over an empty list
     reads as a loading failure. */
  if (items.length === 0) return null;

  const anyUnmet = items.some((item) => !item.isMet);

  return (
    <>
      {items.map((item) => (
        <Gate key={item.key} done={item.isMet}>
          {item.label}
        </Gate>
      ))}

      {anyUnmet && <Note className="mt-s3">{t('startup.checklist.stillAllowed')}</Note>}
    </>
  );
}
